import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Loader2, X, User, Users, GraduationCap, CalendarCheck, Receipt } from 'lucide-react';
import { StudentsApi } from '@/services/school.api';
import {
  ReportsApi,
  StudentPortfolio,
  StudentPortfolioAttendance,
  StudentPortfolioBasic,
  StudentPortfolioFees,
  StudentPortfolioGuardian,
} from '@/services/reports.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { formatDate } from '@/lib/format';
import { fmtMoney } from '@/pages/fees/lib/feeFormat';

export const TABS = [
  { key: 'personal', label: 'Personal Details', icon: User },
  { key: 'guardian', label: 'Guardian Details', icon: Users },
  { key: 'academic', label: 'Academic Results', icon: GraduationCap },
  { key: 'attendance', label: 'Attendance Stats', icon: CalendarCheck },
  { key: 'fees', label: 'Fees Summary', icon: Receipt },
] as const;
export type TabKey = (typeof TABS)[number]['key'];

/**
 * Search-then-single-record view, matching legacy's own
 * StudentPortfolioController shape (confirmed by reading it directly —
 * search returns suggestions, picking one loads a consolidated detail) —
 * a tabbed profile page, not a single flat card list. Academic Results is
 * a placeholder tab (no per-student exam-marks rollup exists in the
 * backend yet — deliberately out of scope for this pass, see
 * reports.service.ts's studentPortfolio() doc comment).
 */
export function StudentPortfolioPage() {
  const [searchInput, setSearchInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const [studentId, setStudentId] = useState('');
  const [tab, setTab] = useState<TabKey>('personal');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: suggestions = [], isFetching: searching } = useQuery({
    queryKey: ['reports', 'portfolio-search', debounced],
    queryFn: () => StudentsApi.list({ search: debounced, limit: 10 }).then((r) => r.items),
    enabled: debounced.length >= 2 && !studentId,
  });

  const { data: portfolio, isFetching: loadingPortfolio } = useQuery({
    queryKey: ['reports', 'portfolio', studentId],
    queryFn: () => ReportsApi.studentPortfolio(studentId),
    enabled: !!studentId,
  });

  const selectStudent = (id: string) => {
    setStudentId(id);
    setTab('personal');
  };

  const clearSelection = () => {
    setStudentId('');
    setSearchInput('');
    setDebounced('');
  };

  return (
    <>
      <PageHeader title="Student Portfolio" description="Search for a student to see a consolidated view — personal, guardian, attendance, and fee details." />

      {!studentId && (
        <div className="relative max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name or admission #"
            className="pl-9"
          />
          {debounced.length >= 2 && (
            <div className="mt-1 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              {searching ? (
                <div className="p-4 text-center text-slate-400">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                </div>
              ) : suggestions.length === 0 ? (
                <div className="p-4 text-center text-sm text-slate-400">No students found.</div>
              ) : (
                suggestions.map((s) => (
                  <button
                    key={s.id}
                    className="flex w-full items-center justify-between px-4 py-2 text-left text-sm hover:bg-slate-50"
                    onClick={() => selectStudent(s.id)}
                  >
                    <span className="font-medium text-slate-900">{s.name ?? '(no linked user)'}</span>
                    <span className="text-slate-400">
                      {s.admissionNo} {s.className ? `· ${s.className} ${s.sectionName}` : ''}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {studentId && (
        <div className="max-w-4xl">
          <button className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" onClick={clearSelection}>
            <X className="h-4 w-4" /> Search another student
          </button>

          {loadingPortfolio || !portfolio ? (
            <div className="card p-8 text-center text-slate-400">
              <Loader2 className="mx-auto h-5 w-5 animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              <PortfolioHeader student={portfolio.student} />

              <div className="flex flex-wrap items-center gap-1 border-b border-slate-200">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    className={`inline-flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium ${
                      tab === t.key ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <t.icon className="h-4 w-4" /> {t.label}
                  </button>
                ))}
              </div>

              {tab === 'personal' && <PersonalTab student={portfolio.student} />}
              {tab === 'guardian' && <GuardianTab guardian={portfolio.guardian} />}
              {tab === 'academic' && <AcademicTab />}
              {tab === 'attendance' && <AttendanceTab attendance={portfolio.attendance} />}
              {tab === 'fees' && <FeesTab fees={portfolio.fees} />}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export function PortfolioHeader({ student }: { student: StudentPortfolioBasic }) {
  const initial = (student.studentName || '?').trim().charAt(0).toUpperCase();
  return (
    <div className="overflow-hidden rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 p-5 text-white shadow-sm">
      <div className="flex flex-wrap items-center gap-4">
        {student.photoUrl ? (
          <img src={student.photoUrl} alt={student.studentName} className="h-16 w-16 rounded-full border-2 border-white/70 object-cover" />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-white/70 bg-white/10 text-2xl font-semibold">
            {initial}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-bold leading-tight">{student.studentName}</h2>
          <p className="text-sm text-white/80">
            {student.className} {student.sectionName} | {student.schoolName}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-medium">Admission No: {student.admissionNo}</span>
            {student.rollNumber != null && <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-medium">Roll No: {student.rollNumber}</span>}
            {student.enrollmentStatus && (
              <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-medium capitalize">
                {student.enrollmentStatus === 'continue' ? 'Active' : 'Left'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-5">
      <h4 className="mb-3 text-sm font-semibold text-slate-700">{title}</h4>
      <dl className="space-y-2.5 text-sm">{children}</dl>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right font-medium text-slate-800">{value !== null && value !== undefined && value !== '' ? value : 'N/A'}</dd>
    </div>
  );
}

export function PersonalTab({ student }: { student: StudentPortfolioBasic }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <InfoCard title="Basic Info">
        <InfoRow label="Gender" value={student.gender ? student.gender.toUpperCase() : null} />
        <InfoRow label="DOB" value={student.dob ? formatDate(student.dob) : null} />
        <InfoRow label="Phone" value={student.mobile} />
        <InfoRow label="Email" value={student.email} />
        <InfoRow label="Username" value={student.username} />
      </InfoCard>
      <InfoCard title="School & Academic Session">
        <InfoRow label="School" value={student.schoolName} />
        <InfoRow label="Class & Division" value={`${student.className} ${student.sectionName}`} />
        <InfoRow label="Medium" value={student.mediumName} />
        <InfoRow label="Session Year" value={student.sessionYearName} />
        <InfoRow label="Admission Date" value={formatDate(student.admissionDate)} />
      </InfoCard>
      <InfoCard title="Address Info">
        <div className="space-y-1">
          <dt className="text-slate-500">Current Address</dt>
          <dd className="whitespace-pre-line font-medium text-slate-800">{student.currentAddress || 'N/A'}</dd>
        </div>
        <div className="space-y-1 pt-2">
          <dt className="text-slate-500">Permanent Address</dt>
          <dd className="whitespace-pre-line font-medium text-slate-800">{student.permanentAddress || 'N/A'}</dd>
        </div>
      </InfoCard>
    </div>
  );
}

export function GuardianTab({ guardian }: { guardian: StudentPortfolioGuardian | null }) {
  if (!guardian) return <div className="card p-8 text-center text-sm text-slate-400">No guardian on record.</div>;
  const initial = guardian.name.trim().charAt(0).toUpperCase();
  return (
    <div className="space-y-4">
      <div className="card flex items-center gap-4 p-5">
        {guardian.photoUrl ? (
          <img src={guardian.photoUrl} alt={guardian.name} className="h-14 w-14 rounded-full object-cover" />
        ) : (
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-lg font-semibold text-slate-500">{initial}</div>
        )}
        <div>
          <div className="text-lg font-semibold text-slate-900">{guardian.name}</div>
          <div className="text-sm text-slate-400">Parent / Guardian</div>
        </div>
      </div>
      <InfoCard title="Contact & Basic Info">
        <InfoRow label="Mobile" value={guardian.mobile} />
        <InfoRow label="Email" value={guardian.email} />
        <InfoRow label="Gender" value={guardian.gender ? guardian.gender.toUpperCase() : null} />
        <InfoRow label="Occupation" value={null} />
      </InfoCard>
    </div>
  );
}

export function AcademicTab() {
  return (
    <div className="space-y-4">
      <div className="card p-5">
        <h4 className="mb-3 text-sm font-semibold text-slate-700">Offline Exam Results</h4>
        <div className="rounded-md bg-slate-50 p-6 text-center text-sm text-slate-400">Academic results aren't available in this view yet.</div>
      </div>
      <div className="card p-5">
        <h4 className="mb-3 text-sm font-semibold text-slate-700">Online Exam Attempts</h4>
        <div className="rounded-md bg-slate-50 p-6 text-center text-sm text-slate-400">Academic results aren't available in this view yet.</div>
      </div>
    </div>
  );
}

export function AttendanceTab({ attendance }: { attendance: StudentPortfolioAttendance | null }) {
  if (!attendance) return <div className="card p-8 text-center text-sm text-slate-400">No attendance recorded.</div>;
  const pct = attendance.percentage != null ? Math.round(attendance.percentage) : null;
  const ringStyle = { background: `conic-gradient(#4f46e5 ${pct ?? 0}%, #e2e8f0 0)` };
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_1fr]">
      <div className="card flex flex-col items-center justify-center gap-3 p-5">
        <div className="text-sm font-semibold text-slate-700">Attendance Percentage</div>
        <div className="relative flex h-32 w-32 items-center justify-center rounded-full" style={ringStyle}>
          <div className="flex h-24 w-24 items-center justify-center rounded-full bg-white text-xl font-bold text-indigo-600">
            {pct != null ? `${pct}%` : '—'}
          </div>
        </div>
      </div>
      <div className="card p-5">
        <h4 className="mb-3 text-sm font-semibold text-slate-700">Attendance Breakdown</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-green-50 p-4 text-center">
            <div className="text-xs font-medium uppercase tracking-wide text-green-700">Total Present</div>
            <div className="mt-1 text-2xl font-bold text-green-700">{attendance.present}</div>
          </div>
          <div className="rounded-lg bg-red-50 p-4 text-center">
            <div className="text-xs font-medium uppercase tracking-wide text-red-700">Total Absent</div>
            <div className="mt-1 text-2xl font-bold text-red-700">{attendance.absent}</div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Badge tone="amber">Late: {attendance.late}</Badge>
          <Badge tone="blue">Half Day: {attendance.halfDay}</Badge>
          <Badge tone="slate">Holiday: {attendance.holiday}</Badge>
        </div>
      </div>
    </div>
  );
}

function feeStatus(balance: number, paidAmount: number): { label: string; tone: 'green' | 'amber' | 'red' } {
  if (balance <= 0) return { label: 'Paid', tone: 'green' };
  if (paidAmount > 0) return { label: 'Partial', tone: 'amber' };
  return { label: 'Pending', tone: 'red' };
}

export function FeesTab({ fees }: { fees: StudentPortfolioFees | null }) {
  if (!fees) return <div className="card p-8 text-center text-sm text-slate-400">No fee records.</div>;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Badge tone="slate">Demanded: {fmtMoney(fees.demanded)}</Badge>
        <Badge tone="green">Paid: {fmtMoney(fees.paid)}</Badge>
        <Badge tone={fees.pending > 0 ? 'red' : 'green'}>Pending: {fmtMoney(fees.pending)}</Badge>
      </div>

      <div className="card overflow-hidden p-0">
        <h4 className="p-5 pb-0 text-sm font-semibold text-slate-700">Fee Demands & Outstanding</h4>
        <div className="overflow-x-auto p-5 pt-3">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
                <th className="pb-2 pr-4">Fee Name</th>
                <th className="pb-2 pr-4">Installment</th>
                <th className="pb-2 pr-4">Actual Amt</th>
                <th className="pb-2 pr-4">Discount</th>
                <th className="pb-2 pr-4">Paid</th>
                <th className="pb-2 pr-4">Due Date</th>
                <th className="pb-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {fees.demands.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-slate-400">
                    No fee demands recorded.
                  </td>
                </tr>
              ) : (
                fees.demands.map((d) => {
                  const status = feeStatus(d.balance, d.paidAmount);
                  return (
                    <tr key={d.id} className="border-b border-slate-100 last:border-0">
                      <td className="py-2 pr-4">{d.feeStructureName}</td>
                      <td className="py-2 pr-4">{d.installmentName ?? '—'}</td>
                      <td className="py-2 pr-4">{fmtMoney(d.actualAmount)}</td>
                      <td className="py-2 pr-4">{fmtMoney(Math.abs(d.discountAmount))}</td>
                      <td className="py-2 pr-4">{fmtMoney(d.paidAmount)}</td>
                      <td className="py-2 pr-4">{formatDate(d.dueDate)}</td>
                      <td className="py-2">
                        <Badge tone={status.tone}>{status.label}</Badge>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        <h4 className="p-5 pb-0 text-sm font-semibold text-slate-700">Payments History</h4>
        <div className="overflow-x-auto p-5 pt-3">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
                <th className="pb-2 pr-4">Fee Name</th>
                <th className="pb-2 pr-4">Installment</th>
                <th className="pb-2 pr-4">Amount Paid</th>
                <th className="pb-2 pr-4">Mode</th>
                <th className="pb-2 pr-4">Payment Date</th>
                <th className="pb-2">Reference</th>
              </tr>
            </thead>
            <tbody>
              {fees.payments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-slate-400">
                    No payment logs found.
                  </td>
                </tr>
              ) : (
                fees.payments.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 pr-4">{p.feeStructureName}</td>
                    <td className="py-2 pr-4">{p.installmentName ?? '—'}</td>
                    <td className="py-2 pr-4">{fmtMoney(p.amount)}</td>
                    <td className="py-2 pr-4 capitalize">{p.paymentMethod ?? '—'}</td>
                    <td className="py-2 pr-4">{formatDate(p.paidDate)}</td>
                    <td className="py-2">{p.paymentReference ?? '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
